<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

  import { ApiError } from "../../../../../../../../../api/kehrblatt/client"
  import {
    adaptCaseCommunicationFeed,
    fetchCaseCommunicationFeed,
    type CaseCommunicationFeedResult,
  } from "../../../../../../../../../api/kehrblatt/case-communications"
  import {
    createContactOrder,
    fetchContactOrders,
    type ContactOrder,
    type ContactOrderCreatePayload,
  } from "../../../../../../../../../api/kehrblatt/contact-orders"
  import { resolveAnnouncementPlanCreateState } from "./_logic/announcementPlanCreateState"
  import {
    buildScheduleEventCommunicationItems,
    findActiveScheduleEventContactOrder,
    findScheduleEventContactOrder,
  } from "./_logic/scheduleEventCommunicationSection"
  import {
    buildContactOrderClosureSubLabel,
    contactOrderClosedReasonLabel,
  } from "../../../../../../../../../_shared/_logic/outreach-presentation"

  type Props = {
    organizationId: string
    event: ScheduleEvent
    workOrder?: WorkOrder | null
    contactPoints?: ContactPoint[]
    customers?: Customer[]
    forceReadOnly?: boolean
    onCreated?: (event: ScheduleEvent, order: ContactOrder) => Promise<void> | void
  }

  let {
    organizationId,
    event,
    workOrder = null,
    contactPoints = [],
    customers = [],
    forceReadOnly = false,
    onCreated = null,
  }: Props = $props()

  let communicationFeed = $state<CaseCommunicationFeedResult>({
    items: [],
    contact_order_summaries: [],
    total: 0,
    next_cursor: null,
  })
  let summaryContactOrders = $state<ContactOrder[]>([])
  let createdContactOrder = $state<ContactOrder | null>(null)
  let communicationsLoading = $state(false)
  let creating = $state(false)
  let expanded = $state(false)

  const caseId = $derived(workOrder?.case_id ?? null)
  const adaptedFeed = $derived(adaptCaseCommunicationFeed(communicationFeed))
  const communications = $derived(adaptedFeed.caseCommunications)
  const contactOrders = $derived.by(() => {
    const scopedOrders = [...adaptedFeed.contactOrders, ...summaryContactOrders]

    return createdContactOrder
      ? [
          createdContactOrder,
          ...scopedOrders.filter((order) => order.id !== createdContactOrder?.id),
        ]
      : scopedOrders
  })
  const effectiveContactPoints = $derived(
    adaptedFeed.contactPoints.length ? adaptedFeed.contactPoints : contactPoints,
  )
  const effectiveCustomers = $derived(
    adaptedFeed.customers.length ? adaptedFeed.customers : customers,
  )
  const contactOrder = $derived(findScheduleEventContactOrder(contactOrders, event))
  const activeContactOrder = $derived(findActiveScheduleEventContactOrder(contactOrders, event))
  const historicalContactOrder = $derived(
    contactOrder && contactOrder.id !== activeContactOrder?.id ? contactOrder : null,
  )
  const createState = $derived(
    resolveAnnouncementPlanCreateState({
      event,
      workOrder,
      activeOrder: activeContactOrder,
      forceReadOnly,
    }),
  )
  const items = $derived(
    buildScheduleEventCommunicationItems({
      communications,
      contactOrder,
      contactPoints: effectiveContactPoints,
      customers: effectiveCustomers,
    }),
  )
  const visibleItems = $derived(expanded ? items : items.slice(0, 4))
  const hasMore = $derived(items.length > visibleItems.length)
  const loading = $derived(communicationsLoading)

  const normalizeCode = (value: unknown): string =>
    String(value ?? "")
      .trim()
      .toUpperCase()

  const historicalPlanPresentation = (order: ContactOrder | null, scheduleEvent: ScheduleEvent) => {
    const closedReason = normalizeCode(order?.closed_reason)
    const eventStatus = normalizeCode(scheduleEvent.status)
    const reasonLabel = contactOrderClosedReasonLabel(order?.closed_reason)
    const subLabel = buildContactOrderClosureSubLabel(order)
    const canceled =
      eventStatus === "CANCELED" ||
      eventStatus === "CANCELLED" ||
      eventStatus === "NOSHOW" ||
      closedReason === "CANCELED" ||
      closedReason === "SUPERSEDED" ||
      closedReason === "EXHAUSTED"

    if (canceled) {
      return {
        title: reasonLabel ? `${reasonLabel}er Ankündigungsplan` : "Abgebrochener Ankündigungsplan",
        message: subLabel ?? "Dieser Ankündigungsplan ist nicht mehr aktiv.",
        status: { value: "CANCELED", label: reasonLabel ?? "Abgebrochen", color: "neutral" },
      }
    }

    return {
      title: reasonLabel ? `${reasonLabel}er Ankündigungsplan` : "Abgeschlossener Ankündigungsplan",
      message:
        subLabel ??
        "Dieser Ankündigungsplan ist abgeschlossen und wird hier nur noch historisch angezeigt.",
      status: { value: "CLOSED", label: reasonLabel ?? "Abgeschlossen", color: "emerald" },
    }
  }

  const historicalPlanState = $derived(historicalPlanPresentation(historicalContactOrder, event))

  const resolveErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof ApiError && error.message?.trim()) return error.message.trim()

    if (error instanceof Error && error.message?.trim()) return error.message.trim()

    return fallback
  }

  const loadCommunications = async () => {
    if (!event.id || communicationsLoading) return

    communicationsLoading = true
    try {
      const items = new Map<string, CaseCommunicationFeedResult["items"][number]>()
      const summaries = new Map<
        string,
        CaseCommunicationFeedResult["contact_order_summaries"][number]
      >()
      const seenCursors = new Set<string>()
      let cursor: string | null = null
      let total = 0

      do {
        const page = await fetchCaseCommunicationFeed({
          organizationId,
          caseId,
          refService: "kehrblatt_control",
          refResource: "schedule_event",
          refId: event.id,
          refRole: "SCHEDULE_EVENT",
          projection: "history",
          sortDirection: "desc",
          limit: 100,
          ...(cursor ? { cursor } : {}),
        })
        for (const item of page.items) items.set(item.entry_key, item)
        for (const summary of page.contact_order_summaries) summaries.set(summary.id, summary)
        total = Math.max(total, page.total)

        const nextCursor = page.next_cursor ?? null

        if (!nextCursor) break

        if (seenCursors.has(nextCursor)) throw new Error("case_communication_feed_cursor_cycle")

        seenCursors.add(nextCursor)
        cursor = nextCursor
      } while (cursor)

      const representedOrderIds = new Set(
        Array.from(items.values()).map((item) => item.contact_order_id),
      )
      const missingOrderIds = new Set(
        Array.from(summaries.keys()).filter((id) => !representedOrderIds.has(id)),
      )
      let missingPlans: ContactOrder[] = []

      if (missingOrderIds.size) {
        if (!caseId) throw new Error("Der Fallbezug des Kontaktplans fehlt.")

        const productPlans = await fetchContactOrders({ caseId })
        missingPlans = productPlans.filter((plan) => missingOrderIds.has(plan.id))

        if (missingPlans.length !== missingOrderIds.size) {
          throw new Error("Die Terminmitteilung zum Ankündigungsplan fehlt.")
        }
      }

      const nextFeed: CaseCommunicationFeedResult = {
        items: Array.from(items.values()),
        contact_order_summaries: Array.from(summaries.values()),
        total: Math.max(total, items.size),
        next_cursor: null,
      }
      adaptCaseCommunicationFeed(nextFeed)
      summaryContactOrders = missingPlans
      communicationFeed = nextFeed
    } catch (err) {
      console.error("schedule_event_communications_load_failed", err)
      newNotification({
        class: "error",
        html: resolveErrorMessage(err, "Terminkommunikation konnte nicht geladen werden."),
      })
    } finally {
      communicationsLoading = false
    }
  }

  onMount(() => {
    void loadCommunications()
  })

  const resolveAssignedEmployeeIds = () => {
    const allowedEmployeeIds = Array.isArray(workOrder?.allowed_employee_ids)
      ? workOrder.allowed_employee_ids.filter(Boolean)
      : []

    if (allowedEmployeeIds.length) return Array.from(new Set(allowedEmployeeIds))

    if (workOrder?.assigned_employee_id) return [workOrder.assigned_employee_id]

    if (event.employee_id) return [event.employee_id]

    return []
  }

  const createPlan = async () => {
    if (!caseId || !workOrder?.id || !event.id || creating) return

    creating = true
    try {
      const payload: ContactOrderCreatePayload = {
        work_order_id: workOrder.id,
        schedule_event_id: event.id,
        replace_existing: false,
        assigned_employee_ids: resolveAssignedEmployeeIds(),
        proposed_start_at: event.start_at,
        proposed_end_at: event.end_at,
      }
      const created = await createContactOrder(caseId, payload)
      createdContactOrder = created
      const updatedEvent: ScheduleEvent = {
        ...event,
        status: "RESERVED" as ScheduleEventStatus,
        contact_order_id: created.id,
      }
      await onCreated?.(updatedEvent, created)
      newNotification({ class: "success", html: "Ankündigungsplan erstellt." })
    } catch (err) {
      console.error("schedule_event_contact_order_create_failed", err)
      newNotification({
        class: "error",
        html: resolveErrorMessage(err, "Ankündigungsplan konnte nicht erstellt werden."),
      })
    } finally {
      creating = false
    }
  }

  const openCommunicationTab = () => {
    if (!organizationId || !workOrder?.id) return

    window.location.href = `/organizations/${organizationId}/work-orders/${workOrder.id}/communications`
  }
</script>

<section class="schedule-event-communication-section">
  <SectionTitle
    small={true}
    title="Terminkommunikation"
    style="margin-top: 0.9rem; margin-bottom: 0.25rem;"
  />
  {#if loading && !items.length}
    <p class="muted">Terminkommunikation wird geladen…</p>
  {:else}
    {#if items.length}
      <ol>
        {#each visibleItems as item (item.id)}
          <li>
            <div class="item-heading">
              <strong>{item.title}</strong>
              <StatusTag size="sm" status={item.status} disableTitle={true} />
            </div>
            {#if item.facts.length}
              <dl class="item-facts">
                {#each item.facts as fact}
                  <div>
                    <dt>{fact.label}</dt>
                    <dd>{fact.value}</dd>
                  </div>
                {/each}
              </dl>
            {/if}
            <p>{item.detail}</p>
          </li>
        {/each}
      </ol>
    {/if}

    {#if hasMore}
      <div class="section-actions">
        <CTA
          type="tertiaryButton"
          small={true}
          content={expanded ? "Weniger anzeigen" : `Alle anzeigen (${items.length})`}
          onClick={() => (expanded = !expanded)}
        />
      </div>
    {/if}

    {#if activeContactOrder}
      <div class="plan-state">
        <div class="item-heading">
          <strong>Aktiver Ankündigungsplan</strong>
          <StatusTag
            size="sm"
            status={{ value: "ACTIVE", label: "Aktiv", color: "blue" }}
            disableTitle={true}
          />
        </div>
        {#if !activeContactOrder.steps?.length}
          <p>Noch keine Schritte vorhanden.</p>
        {/if}
        <div class="section-actions">
          <CTA
            type="secondaryButton"
            small={true}
            content="Öffnen"
            onClick={openCommunicationTab}
          />
        </div>
      </div>
    {:else if historicalContactOrder}
      <div class="plan-state">
        <div class="item-heading">
          <strong>{historicalPlanState.title}</strong>
          <StatusTag size="sm" status={historicalPlanState.status} disableTitle={true} />
        </div>
        <p>{historicalPlanState.message}</p>
        <div class="section-actions">
          <CTA
            type="secondaryButton"
            small={true}
            content="Öffnen"
            onClick={openCommunicationTab}
          />
        </div>
      </div>
    {:else if createState.message}
      <div class="plan-state">
        <div class="item-heading">
          <strong>{createState.actionBoxLabel}</strong>
          <StatusTag
            size="sm"
            status={{
              value: createState.reason,
              label: createState.actionBoxLabel,
              color: createState.reason === "completed" ? "emerald" : "blue",
            }}
            disableTitle={true}
          />
        </div>
        <p>{createState.message}</p>
      </div>
    {:else}
      <div class="plan-state">
        <div class="item-heading">
          <strong>{createState.actionBoxLabel}</strong>
          <StatusTag
            size="sm"
            status={{ value: "ALLOWED", label: "Möglich", color: "neutral" }}
            disableTitle={true}
          />
        </div>
        <p>Für diesen Termin kann ein Ankündigungsplan angelegt werden.</p>
        <div class="section-actions">
          <CTA
            type="primaryButton"
            content={creating ? "Erstelle…" : "Ankündigungsplan erstellen"}
            disabled={!createState.canCreate || creating}
            onClick={createPlan}
          />
        </div>
      </div>
    {/if}
  {/if}
</section>

<style lang="less">
  .schedule-event-communication-section {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }

  ol {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li,
  .plan-state {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-left: 2px solid
      color-mix(in srgb, var(--overlay-text-color, var(--text-100)) 18%, transparent);
    padding-left: 0.65rem;
  }

  .plan-state {
    margin-top: 0.15rem;
  }

  .item-heading {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  .item-heading strong {
    color: var(--overlay-text-color, var(--text-100));
    min-width: 0;
    overflow-wrap: anywhere;
  }

  p,
  .muted {
    margin: 0;
    color: var(--text-60);
    line-height: 1.35;
  }

  .item-facts {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin: 0;
  }

  .item-facts div {
    display: grid;
    grid-template-columns: minmax(5.4rem, max-content) minmax(0, 1fr);
    gap: 0.45rem;
    align-items: baseline;
  }

  .item-facts dt {
    color: var(--text-60);
    font-family: var(--font-family-bold, "Outfit-Bold", sans-serif);
    font-size: 0.82rem;
  }

  .item-facts dd {
    margin: 0;
    color: var(--text-80);
    overflow-wrap: anywhere;
  }

  .section-actions {
    display: flex;
    justify-content: flex-start;
    margin-top: 0.15rem;
  }
</style>
